import React, { useEffect, useState } from 'react';
import { Fingerprint, Check, Loader2, X } from 'lucide-react';
import {
  passkeySupported,
  isPlatformAuthenticatorAvailable,
  registerAdminPasskey,
  registerDriverPasskey,
} from '../lib/passkey';
import { useToast } from '../hooks/use-toast';

/**
 * PasskeyEnrollCard — drop-in card for admin or driver dashboards.
 *
 * Props:
 *   role: 'admin' | 'driver'
 *   authHeaders: Bearer headers for current session
 *   variant: 'dark' (dispatch dark theme) | 'light' (admin glass theme)
 *   storageKey: localStorage flag to remember dismissal (per device)
 */
const PasskeyEnrollCard = ({ role, authHeaders, variant = 'light', storageKey }) => {
  const { toast } = useToast();
  const [supported, setSupported] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(storageKey || `mr_passkey_enroll_${role}_dismissed`) === '1'; } catch { return false; }
  });
  const [enrolled, setEnrolled] = useState(() => {
    try { return localStorage.getItem(`mr_passkey_enrolled_${role}`) === '1'; } catch { return false; }
  });

  useEffect(() => {
    setSupported(passkeySupported());
  }, []);

  const enroll = async () => {
    setBusy(true);
    try {
      if (role === 'admin') await registerAdminPasskey(authHeaders);
      else await registerDriverPasskey(authHeaders);
      setEnrolled(true);
      try { localStorage.setItem(`mr_passkey_enrolled_${role}`, '1'); } catch (e) { /* ignore */ }
      toast({ title: 'Passkey enabled ✓', description: 'Next time, sign in with fingerprint or Face ID.' });
    } catch (err) {
      toast({ title: 'Could not enable passkey', description: err?.response?.data?.detail || err?.message || 'Try again' });
    } finally {
      setBusy(false);
    }
  };

  const dismiss = () => {
    setDismissed(true);
    try { localStorage.setItem(storageKey || `mr_passkey_enroll_${role}_dismissed`, '1'); } catch (e) { /* ignore */ }
  };

  if (!supported || dismissed) return null;

  const dark = variant === 'dark';
  const wrap = dark
    ? 'bg-emerald-500/10 border border-emerald-500/30 text-white'
    : 'bg-blue-50 border border-blue-200 text-[#0F172A]';
  const accent = dark ? 'text-emerald-300' : 'text-[#0078D4]';
  const btn = dark
    ? 'bg-emerald-500 hover:bg-emerald-600 text-white'
    : 'bg-[#0078D4] hover:bg-[#005A9E] text-white';

  return (
    <div
      className={`relative rounded-2xl px-4 py-3 flex items-center gap-3 ${wrap}`}
      data-testid={`passkey-enroll-${role}`}
    >
      <div className={`shrink-0 w-10 h-10 rounded-xl ${dark ? 'bg-emerald-500/20' : 'bg-blue-100'} flex items-center justify-center`}>
        {enrolled ? (
          <Check className={`w-5 h-5 ${accent}`} />
        ) : (
          <Fingerprint className={`w-5 h-5 ${accent}`} />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold">
          {enrolled ? 'Passkey is on this device' : 'Sign in faster with fingerprint / Face ID'}
        </div>
        <div className={`text-xs mt-0.5 ${dark ? 'text-slate-300' : 'text-neutral-600'}`}>
          {enrolled
            ? 'Use it next time you sign in — no password needed.'
            : 'Skip the password — your device unlocks the portal in one tap.'}
        </div>
      </div>
      {!enrolled && (
        <button
          type="button"
          onClick={enroll}
          disabled={busy}
          className={`shrink-0 h-9 px-3 rounded-full text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5 ${btn} disabled:opacity-50`}
          data-testid={`passkey-enroll-btn-${role}`}
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Fingerprint className="w-3.5 h-3.5" />}
          Enable
        </button>
      )}
      <button
        type="button"
        onClick={dismiss}
        className={`absolute top-2 right-2 ${dark ? 'text-slate-400 hover:text-white' : 'text-neutral-400 hover:text-neutral-700'}`}
        aria-label="Dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

export default PasskeyEnrollCard;
